import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSession, signOut } from '../../lib/authClient';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, Bell, User, Settings, LogOut, CheckCheck, ExternalLink } from 'lucide-react';
import { notificationsAPI, accountAPI } from '../../services/api';
import Badge from '../UI/Badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/UI/dropdown-menu';

const mainNav = [
  { label: 'Home', section: 'home', defaultPath: '/home/dashboard' },
  { label: 'Strategies', section: 'strategies', defaultPath: '/strategies/manager' },
  { label: 'Analysis', section: 'analysis', defaultPath: '/analysis/backtesting' },
  { label: 'Apollo', section: 'apollo', defaultPath: '/apollo/brokers' },
];

const TYPE_STYLE = {
  error:   { variant: 'danger',  label: 'Error' },
  warning: { variant: 'warning', label: 'Warning' },
  info:    { variant: 'info',    label: 'Info' },
};

function getNotifBadge(n) {
  const side = n.context?.side?.toLowerCase();
  if (side === 'buy') return { variant: 'success', label: 'BUY' };
  if (side === 'sell') return { variant: 'danger', label: 'SELL' };
  return TYPE_STYLE[n.type] || TYPE_STYLE.info;
}

function getInitials(name) {
  if (!name) return '?';
  return name
    .split(' ')
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

function Topbar({ onMenuClick, showHamburger }) {
  const { data: session } = useSession();
  const navigate = useNavigate();
  const location = useLocation();

  const currentSection = location.pathname.split('/')[1];

  // --- License tier state ---
  const [tier, setTier] = useState(null);

  useEffect(() => {
    if (!session?.user) return;
    accountAPI.getLicense()
      .then((res) => setTier(res.data?.tier || 'basic'))
      .catch(() => setTier('basic'));
  }, [session?.user]);

  // --- Notifications state ---
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const notifRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const [listRes, countRes] = await Promise.all([
        notificationsAPI.getAll({ limit: 5 }),
        notificationsAPI.getUnreadCount(),
      ]);
      setNotifications(listRes.data ?? []);
      setUnreadCount(countRes.count ?? 0);
    } catch {
      // silent — don't block UI for notification fetch failures
    }
  }, []);

  // Fetch on mount and poll every 30s
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Re-fetch when dropdown opens
  useEffect(() => {
    if (notifOpen) fetchNotifications();
  }, [notifOpen, fetchNotifications]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setNotifOpen(false);
      }
    };
    if (notifOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [notifOpen]);

  async function handleMarkAllRead() {
    try {
      await notificationsAPI.markAllRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {
      // silent
    }
  }

  async function handleLogout() {
    await signOut();
    navigate('/signin');
  }

  const user = session?.user;
  const initials = getInitials(user?.name);

  const TIER_COLORS = {
    basic:     'bg-zinc-600',
    core:      'bg-blue-500',
    pro:       'bg-purple-500',
    custom:    'bg-amber-500',
    unlimited: 'bg-emerald-500',
  };
  const TIER_BADGE_VARIANT = {
    basic:     'neutral',
    core:      'info',
    pro:       'default',
    custom:    'warning',
    unlimited: 'success',
  };

  return (
    <header className="h-14 bg-card/60 backdrop-blur-xl border-b border-white/10 flex items-center px-4 gap-1 flex-shrink-0 select-none relative z-50">
      {/* Hamburger for mobile */}
      {showHamburger && (
        <button
          onClick={onMenuClick}
          className="w-10 h-10 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent/10 transition-colors mr-2"
          aria-label="Toggle menu"
        >
          <Menu className="w-5 h-5" />
        </button>
      )}

      {/* Mobile section label */}
      <span className="text-sm font-medium text-foreground capitalize md:hidden">{currentSection}</span>

      {/* Main navigation */}
      <nav className="hidden md:flex items-center gap-1 flex-1">
        {mainNav.map(({ label, section, defaultPath }) => {
          const isActive = currentSection === section;
          return (
            <button
              key={section}
              onClick={() => navigate(defaultPath)}
              className={`px-4 h-14 text-sm font-medium transition-colors border-b-2 ${
                isActive
                  ? 'text-foreground border-primary'
                  : 'text-muted-foreground border-transparent hover:text-foreground hover:border-muted-foreground/30'
              }`}
            >
              {label}
            </button>
          );
        })}
      </nav>

      {/* Spacer for mobile */}
      <div className="flex-1 md:hidden" />

      {/* Right controls */}
      <div className="flex items-center gap-2">
        {/* Notifications */}
        <div className="relative" ref={notifRef}>
          <button
            onClick={() => setNotifOpen((o) => !o)}
            className="w-10 h-10 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent/10 transition-colors relative"
            title="Notifications"
          >
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold leading-none">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          <AnimatePresence>
            {notifOpen && (
              <>
              {/* Backdrop — mobile only */}
              <motion.div
                key="notif-backdrop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 z-[99] sm:hidden"
                onClick={() => setNotifOpen(false)}
              />
              <motion.div
                key="notif-panel"
                initial={{ opacity: 0, y: -8, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -8, scale: 0.96 }}
                transition={{ duration: 0.15, ease: 'easeOut' }}
                className="fixed inset-x-4 top-16 sm:absolute sm:inset-auto sm:right-0 sm:top-full sm:mt-2 sm:w-96 bg-card border border-border rounded-xl shadow-2xl z-[100] overflow-hidden max-h-[80vh] sm:max-h-none flex flex-col"
              >
                {/* Header */}
                <div className="px-4 py-3 border-b border-border">
                  <p className="text-sm font-semibold text-foreground">Notifications</p>
                </div>

                {/* Notification list */}
                <div className="max-h-80 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                      No notifications
                    </div>
                  ) : (
                    notifications.map((n) => {
                      const badge = getNotifBadge(n);
                      return (
                        <div
                          key={n.id}
                          onClick={() => {
                            if (!n.read) {
                              notificationsAPI.markRead(n.id).catch(() => {});
                              setNotifications((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
                              setUnreadCount((c) => Math.max(0, c - 1));
                            }
                            setNotifOpen(false);
                            navigate(n.strategy_id ? `/strategies/trading-floor/${n.strategy_id}` : '/strategies/trading-floor');
                          }}
                          className={`px-4 py-3 border-b border-border/50 last:border-b-0 transition-colors hover:bg-white/[0.02] cursor-pointer ${
                            n.read ? 'opacity-50' : ''
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-0.5">
                            {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-cyan-500 flex-shrink-0" />}
                            <Badge variant={badge.variant}>{badge.label}</Badge>
                            <span className="text-xs font-semibold text-foreground truncate">{n.title}</span>
                          </div>
                          <p className="text-xs text-muted-foreground line-clamp-2 ml-[calc(0.375rem+0.5rem)]">{n.message}</p>
                          <div className="flex items-center gap-2 mt-0.5 ml-[calc(0.375rem+0.5rem)]">
                            {n.strategy_name && (
                              <span className="text-[10px] text-cyan-400/70 font-medium">{n.strategy_name}</span>
                            )}
                            <span className="text-[10px] text-muted-foreground/50">
                              {new Date(n.created_at).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Footer actions */}
                <div className="px-3 py-2 border-t border-border flex items-center justify-between gap-2">
                  <button
                    onClick={() => { setNotifOpen(false); navigate('/account/notifications'); }}
                    className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    See all
                  </button>
                  <button
                    onClick={handleMarkAllRead}
                    disabled={unreadCount === 0}
                    className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <CheckCheck className="w-3.5 h-3.5" />
                    Mark all read
                  </button>
                </div>
              </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>

        {/* User avatar dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="relative w-10 h-10 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity"
              title={user?.name || 'Account'}
            >
              {user?.name ? initials : <User className="w-5 h-5" />}
              {tier && tier !== 'basic' && (
                <span className={`absolute -bottom-1 -right-1 w-3 h-3 rounded-full border-2 border-card ${TIER_COLORS[tier] || TIER_COLORS.basic}`} />
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel>
              <p className="text-sm font-medium truncate">{user?.name || 'User'}</p>
              <div className="flex items-center gap-1.5">
                <p className="text-xs text-muted-foreground truncate">{user?.email || ''}</p>
              </div>
              {tier && (
                <Badge variant={TIER_BADGE_VARIANT[tier] || 'neutral'} className="mt-1 text-[10px] px-1.5 py-0">
                  {tier.charAt(0).toUpperCase() + tier.slice(1)}
                </Badge>
              )}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/account/portfolio')}>
              <User className="mr-2 h-4 w-4" />
              Account
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/settings/general')}>
              <Settings className="mr-2 h-4 w-4" />
              Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="text-red-400 focus:text-red-400">
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}

export default Topbar;
